import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import certificate1 from "../../image/certificate1.webp";
import certificate2 from "../../image/certificate2.webp";
import certificate3 from "../../image/certificate3.webp";
import certificate4 from "../../image/certificate4.webp";
import certificate5 from "../../image/certificate5.webp";

function Certificate() {
  const { t } = useTranslation();
  const certificateRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const cert = certificateRef.current;
      if (!cert) return;
      const rect = cert.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const totalHeight = cert.scrollHeight;
      // Calculate how much of Certificate is visible
      const scrolled = Math.min(
        Math.max(windowHeight - rect.top, 0),
        totalHeight
      );
      const maxScroll = totalHeight - windowHeight;
      const percent =
        maxScroll > 0
          ? ((window.scrollY - cert.offsetTop) / maxScroll) * 100
          : 0;
      setProgress(Math.max(0, Math.min(100, percent)));
    };
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  return (
    <div
      ref={certificateRef}
      className="Certificate"
      style={{
        marginTop: isMobile ? "80px" : "7.81vw",
        marginBottom: isMobile ? "80px" : "7.81vw",
        minHeight: isMobile ? "50vh" : "70vh",
        display: "flex",
        flexWrap: "wrap",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "center",
        alignItems: isMobile ? "center" : "flex-start",
        gap: isMobile ? 20 : "2.08vw",
        background: "#fff",
        fontFamily: "Prompt, sans-serif",
        padding: isMobile ? "0 16px" : "0",
        ...(isMobile
          ? {}
          : { zoom: "1", transform: "scale(1)", transformOrigin: "top left" }),
      }}
    >
      {/* Left Section */}
      <div
        style={{
          flex: isMobile ? "none" : "0 0 35%",
          maxWidth: isMobile ? "100%" : "40%",
          width: isMobile ? "100%" : "auto",
          minWidth: isMobile ? "auto" : 320,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: isMobile ? "center" : "flex-start",
          textAlign: isMobile ? "center" : "left",
          padding: isMobile ? "0 16px" : "0 1.25vw",
          boxSizing: "border-box",
          position: isMobile ? "static" : "sticky",
          top: isMobile ? "auto" : "5.21vw",
          zIndex: 2,
          height: "fit-content",
          ...(isMobile ? {} : { zoom: "1", transform: "scale(1)" }),
        }}
      >
        {/* Progress Bar */}
        <div
          style={{
            height: isMobile ? 8 : "0.42vw",
            width: isMobile ? 120 : "8.33vw",
            background: "#eee",
            borderRadius: isMobile ? 4 : "0.21vw",
            marginBottom: isMobile ? 16 : "1.25vw",
            marginTop: isMobile ? 24 : "1.25vw",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${progress}%`,
              background: "linear-gradient(90deg, #f53f3f 0%, #ff6f61 100%)",
              borderRadius: isMobile ? 4 : "0.21vw",
              transition: "width 0.2s",
              position: "absolute",
              left: 0,
              top: 0,
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: isMobile ? 24 : "2.5vw",
            marginTop: isMobile ? 32 : "2vw",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 8 : 0,
          }}
        >
          <span
            style={{
              fontSize: isMobile ? 36 : "3.13vw",
              fontWeight: 600,
              color: "#222",
              letterSpacing: isMobile ? -1 : "-0.05vw",
            }}
          >
            {t('home.certificate.title')}
          </span>
          <span
            style={{
              marginLeft: isMobile ? 150 : "-0.6vw",
              marginTop: isMobile ? 0 : "0.42vw",
              transform: isMobile ? "translateY(-60px)" : "translateY(-20px)",
            }}
          >
            <svg
              width={isMobile ? 32 : "2.5vw"}
              height={isMobile ? 32 : "2.5vw"}
              viewBox="0 0 48 48"
              fill="none"
              style={{
                animation: "tilt-spark-svg 1.2s ease-in-out infinite",
                display: "inline-block",
              }}
            >
              <line
                x1="30"
                y1="9"
                x2="41"
                y2="3"
                stroke="#ff6f61"
                strokeWidth="3.5"
                strokeLinecap="round"
              />
              <line
                x1="35"
                y1="18"
                x2="44"
                y2="17"
                stroke="#ff6f61"
                strokeWidth="3.5"
                strokeLinecap="round"
              />
              <line
                x1="36"
                y1="27"
                x2="45"
                y2="30"
                stroke="#ff6f61"
                strokeWidth="3.5"
                strokeLinecap="round"
              />
            </svg>
            <style>{`
              @keyframes tilt-spark-svg {
                0% { transform: rotate(-40deg); }
                50% { transform: rotate(-30deg); }
                100% { transform: rotate(-40deg); }
              }
            `}</style>
          </span>
        </div>
        <div
          style={{
            fontSize: isMobile ? 16 : "1.04vw",
            color: "#444",
            marginBottom: isMobile ? 24 : "2.5vw",
            lineHeight: 1.5,
            maxWidth: isMobile ? "100%" : "26.04vw",
          }}
        >
          {t('home.certificate.description')}
        </div>
        <a href="#our-services" style={{ textDecoration: "none" }}>
          <a href="https://www.dbd.go.th/" target="_blank" rel="www.dbd.go.th">
            <img
              src={require("../../image/dbd-certified.png")}
              alt="DBD Certified Accounting Practice"
              style={{
                maxWidth: "16vw",
                width: "100%",
                height: "auto",
                background: "white",
                borderRadius: "12px",
                marginTop: "10px",
              }}
            />
          </a>
          <button
            style={{
              background: "#1a232c",
              color: "#fff",
              fontSize: isMobile ? 16 : "0.94vw",
              fontWeight: 500,
              border: "none",
              borderRadius: isMobile ? 32 : "1.67vw",
              padding: isMobile ? "14px 32px" : "0.83vw 1.98vw",
              cursor: "pointer",
              boxShadow: isMobile
                ? "0 2px 8px rgba(0,0,0,0.08)"
                : "0 0.1vw 0.42vw rgba(0,0,0,0.08)",
              display: "flex",
              alignItems: "center",
              gap: isMobile ? 12 : "0.63vw",
              marginTop: isMobile ? 24 : "4.17vw",
            }}
          >
            {t('home.certificate.ctaButton')}
            <span
              style={{
                display: "inline-block",
                transform: isMobile ? "translateY(1px)" : "translateY(0.05vw)",
              }}
            >
              <svg
                width={isMobile ? 24 : "1.46vw"}
                height={isMobile ? 24 : "1.46vw"}
                viewBox="0 0 28 28"
                fill="none"
              >
                <path
                  d="M8 14h12M16 10l4 4-4 4"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>
        </a>
      </div>
      {/* Right Section */}
      <div
        style={{
          flex: isMobile ? "none" : "0 0 50%",
          width: isMobile ? "100%" : "auto",
          maxWidth: "100%",
          minWidth: 0,
          position: "relative",
          display: "flex",
          alignItems: "center", // ปรับให้ภาพอยู่ตรงกลางแนวตั้ง
          justifyContent: "center", // ปรับให้ภาพอยู่ตรงกลางแนวนอน
          boxSizing: "border-box",
          minHeight: isMobile ? "1400px" : "62.5vw",
          marginTop: isMobile ? 24 : "3.13vw",
          ...(isMobile ? {} : { zoom: "1", transform: "scale(1)" }),
        }}
      >
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center", // ปรับให้ภาพอยู่ตรงกลางแนวนอน
            justifyContent: "center", // ปรับให้ภาพอยู่ตรงกลางแนวตั้ง
            gap: isMobile ? 32 : "6.25vw", // เพิ่มช่องว่างระหว่างภาพ
          }}
        >
          {/* รูปภาพที่ 1 */}
          <img
            src={certificate1}
            alt="Associates"
            style={{
              width: isMobile ? "100%" : "41.67vw",
              height: isMobile ? "300px" : "29.43vw",
              borderRadius: isMobile ? 16 : "1.67vw",
              objectFit: "cover",
              border: isMobile
                ? "1px solid rgba(0, 0, 0, 0.1)"
                : "0.05vw solid rgba(0, 0, 0, 0.1)",
              boxShadow: isMobile
                ? "0 4px 24px rgba(0,0,0,0.10)"
                : "0 0.21vw 1.25vw rgba(0,0,0,0.10)",
            }}
          />

          {/* รูปภาพที่ 2 */}
          <img
            src={certificate2}
            alt="Associates"
            style={{
              width: isMobile ? "100%" : "41.67vw",
              height: isMobile ? "300px" : "29.43vw",
              borderRadius: isMobile ? 16 : "1.67vw",
              objectFit: "cover",
              border: isMobile
                ? "1px solid rgba(0, 0, 0, 0.1)"
                : "0.05vw solid rgba(0, 0, 0, 0.1)",
              boxShadow: isMobile
                ? "0 4px 24px rgba(0,0,0,0.10)"
                : "0 0.21vw 1.25vw rgba(0,0,0,0.10)",
            }}
          />

          {/* รูปภาพที่ 3 */}
          <img
            src={certificate3}
            alt="Associates"
            style={{
              height: isMobile ? "100%" : "52.08vw",
              width: isMobile ? "300px" : "37.5vw",
              borderRadius: isMobile ? 16 : "1.67vw",
              objectFit: "cover",
              boxShadow: isMobile
                ? "0 4px 24px rgba(0,0,0,0.10)"
                : "0 0.21vw 1.25vw rgba(0,0,0,0.10)",
            }}
          />

          {/* รูปภาพที่ 4 */}
          <img
            src={certificate4}
            alt="Associates"
            style={{
              height: isMobile ? "100%" : "52.08vw",
              width: isMobile ? "300px" : "37.5vw",
              borderRadius: isMobile ? 16 : "1.67vw",
              objectFit: "cover",
              boxShadow: isMobile
                ? "0 4px 24px rgba(0,0,0,0.10)"
                : "0 0.21vw 1.25vw rgba(0,0,0,0.10)",
            }}
          />

          {/* รูปภาพที่ 5 */}
          <img
            src={certificate5}
            alt="Associates"
            style={{
              height: isMobile ? "100%" : "52.08vw",
              width: isMobile ? "280px" : "35.42vw",
              borderRadius: isMobile ? 16 : "1.67vw",
              objectFit: "cover",
              boxShadow: isMobile
                ? "0 4px 24px rgba(0,0,0,0.10)"
                : "0 0.21vw 1.25vw rgba(0,0,0,0.10)",
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default Certificate;
